<template>
  <div class="messageNotifications" v-loading="listLoading">
    <div class="messageNotificationsHeader">
      <h2 class="messageNotificationsTitle">消息通知</h2>
      <button
        type="button"
        class="messageNotificationsMarkAllBtn"
        :disabled="!hasUnread"
        @click="handleMarkAllRead"
      >
        全部标记为已读
      </button>
    </div>

    <div class="messageNotificationsTabs">
      <button
        v-for="tab in filterTabs"
        :key="tab.value"
        type="button"
        class="messageNotificationsTab"
        :class="{ 'is-active': activeFilter === tab.value }"
        @click="switchFilter(tab.value)"
      >
        {{ tab.label }}
        <span
          v-if="tab.value === 'unread' && unreadCount > 0"
          class="unread-badge"
          >{{ unreadCount > 99 ? '99+' : unreadCount }}</span
        >
      </button>
    </div>

    <div v-if="messageList.length" class="messageNotificationsList">
      <div
        v-for="item in messageList"
        :key="item.id"
        class="messageNotificationsItem"
        :class="{ 'is-unread': item.isRead === READ_STATUS.UNREAD }"
      >
        <div class="messageNotificationsDotWrap">
          <span
            v-if="item.isRead === READ_STATUS.UNREAD"
            class="unread-dot"
          ></span>
        </div>
        <div class="messageNotificationsBody">
          <div class="messageNotificationsItemHead">
            <div class="messageNotificationsTitleWrap">
              <h3 class="messageNotificationsItemTitle">{{ item.title }}</h3>
              <!-- 审核结果状态标签 -->
              <span v-if="item.auditResult === 1" class="msg-tag tag-success"
                >审核通过</span
              >
              <span v-if="item.auditResult === 2" class="msg-tag tag-error"
                >审核未通过</span
              >

              <!-- 到期时间标签 (messageType=3 且存在 expireDays) -->
              <span
                v-if="item.messageType === 3 && item.expireDays != null"
                class="msg-tag"
                :class="item.expireDays <= 10 ? 'tag-error' : 'tag-warning'"
              >
                {{ item.expireDays }}天到期
              </span>
            </div>
            <span class="messageNotificationsItemTime">{{
              item.createTime
            }}</span>
          </div>
          <p class="messageNotificationsItemContent">{{ item.content }}</p>
          <div class="messageNotificationsAction">
            <el-button
              type="primary"
              size="mini"
              class="view-btn"
              @click="handleItemClick(item)"
              >立即查看</el-button
            >
          </div>
        </div>
      </div>
    </div>

    <el-empty
      v-else
      class="messageNotificationsEmpty"
      description="暂无消息"
      :image-size="120"
    />
  </div>
</template>

<script>
import { getMessageList, markMessageRead, markAllMessageRead, getUnreadMessageCount } from "@/api/lx";

const FILTER_TABS = [
  { label: "全部", value: "all" },
  { label: "未读", value: "unread" },
  { label: "已读", value: "read" },
];
// 消息类型：1-业绩返利待确认、2-新课上线、3-协议到期提醒、4-物料申请通知、5-License账号申请
// 审核状态：1-未审核、2-审核通过、3-审核驳回

const READ_STATUS = {
  UNREAD: 1,
  READ: 2,
};

/**
 * 消息通知模块（预览版）
 * 对应设计稿：筛选 Tab、消息列表、分页
 * 后续接入接口时，替换 fetchMessageList 内逻辑即可
 */
export default {
  name: "MessageNotifications",
  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      READ_STATUS,
      listLoading: false,
      activeFilter: "all",
      filterTabs: FILTER_TABS,
      messageList: [],
      unreadCount: 0,
    };
  },
  computed: {
    hasUnread() {
      return this.unreadCount > 0;
    },
  },
  watch: {
    company: {
      handler() {
        this.fetchMessageList();
      },
      deep: true,
    },
    // Filter watch removed, handled in switchFilter
  },
  created() {
    this.fetchMessageList();
  },
  methods: {
    /**
     * 根据当前过滤条件获取消息列表
     * 会将后端的返回数据结构映射为组件所需的格式
     */
    fetchMessageList() {
      this.listLoading = true;
      const params = {};
      if (this.activeFilter === "unread") params.isRead = READ_STATUS.UNREAD;
      if (this.activeFilter === "read") params.isRead = READ_STATUS.READ;

      getMessageList(params)
        .then((res) => {
          if (res.code === 200) {
            const data = res.data || {};
            const records = Array.isArray(data) ? data : data || [];
            this.messageList = [...records];
          } else {
            this.messageList = [];
          }
        })
        .finally(() => {
          this.listLoading = false;
        });

      // 获取右上角未读数角标
      getUnreadMessageCount().then((res) => {
        if (res.code === 200) {
          this.unreadCount = res.data || 0;
        }
      }).catch(() => {});
    },

    /**
     * 切换消息过滤 Tab (全部/未读/已读)
     * @param {string} value 选中的 tab 的 value
     */
    switchFilter(value) {
      if (this.activeFilter === value) return;
      this.activeFilter = value;
      this.fetchMessageList();
    },

    /**
     * 将全部消息标记为已读
     * 调用接口成功后刷新当前列表
     */
    handleMarkAllRead() {
      if (!this.hasUnread) return;
      markAllMessageRead().then((res) => {
        if (res.code === 200) {
          this.$message.success("已全部标记为已读");
          this.fetchMessageList();
          this.$root.$emit('refreshUnreadCount');
        }
      });
    },

    /**
     * 点击单条消息时触发
     * 如果该消息未读，则调用单条已读接口更新状态
     * @param {Object} item 消息项数据对象
     */
    handleItemClick(item) {
      if (item.isRead === READ_STATUS.UNREAD) {
        markMessageRead(item.id).then((res) => {
          if (res.code === 200) {
            this.fetchMessageList();
            this.$root.$emit('refreshUnreadCount');
          }
        });
      }

      const type = item.messageType;
      // 1-业绩返利待确认、2-新课上线、3-协议到期提醒、4-物料申请通知、5-License账号申请
      switch (type) {
        case 1:
          this.$emit("jump", "performance");
          break;
        case 2:
          this.$router.push({ path: "/school/index" });
          break;
        case 3:
          this.$emit("jump", "contract");
          break;
        case 4:
          this.$emit("open-material");
          break;
        case 5:
          this.$emit("jump", "licenseApply");
          break;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.messageNotifications {
  width: 100%;
}
.messageNotificationsHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}
.messageNotificationsTitle {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.4;
}
.messageNotificationsMarkAllBtn {
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  color: #00d8c3;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
}
.messageNotificationsTabs {
  display: flex;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 32px;
}
.messageNotificationsTab {
  padding: 12px 24px;
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #9ca3af;
  cursor: pointer;
  position: relative;
  transition: color 0.2s, border-color 0.2s;
  &:hover {
    color: #6b7280;
  }
  &.is-active {
    color: #00d8c3;
    border-bottom-color: #00d8c3;
  }
}
.messageNotificationsList {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 8px;
}
.messageNotificationsList::-webkit-scrollbar {
  width: 6px;
}
.messageNotificationsList::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 3px;
}
.messageNotificationsList::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
.messageNotificationsList::-webkit-scrollbar-track {
  background: transparent;
}
.messageNotificationsItem {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 20px;
  border: 1px solid #f9fafb;
  border-radius: 2px;
  background: #fff;
  transition: background-color 0.2s;
  &:hover {
    background: #f9fafb;
  }
  &.is-unread {
    background: #f9fafb;
    border-color: #f3f4f6;
    .messageNotificationsItemTitle {
      font-weight: 600;
      color: #111827;
    }
  }
}
.messageNotificationsDotWrap {
  width: 8px;
  flex-shrink: 0;
  margin-top: 6px;
}
.unread-dot {
  display: block;
  width: 8px;
  height: 8px;
  background-color: #ff4d4f;
  border-radius: 50%;
}
.messageNotificationsTitleWrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.msg-tag {
  display: inline-block;
  padding: 2px 6px;
  font-size: 12px;
  border-radius: 2px;
  line-height: 1.2;
}
.tag-success {
  color: #52c41a;
  background-color: #f6ffed;
}
.tag-error {
  color: #ff4d4f;
  background-color: #fff2f0;
}
.tag-warning {
  color: #faad14;
  background-color: #fffbe6;
}
.messageNotificationsAction {
  margin-top: 12px;
}
.view-btn {
  border-radius: 2px;
  font-size: 12px;
}
.messageNotificationsBody {
  flex: 1;
  min-width: 0;
}
.messageNotificationsItemHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.messageNotificationsItemTitle {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.messageNotificationsItemTime {
  flex-shrink: 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
}
.messageNotificationsItemContent {
  margin: 8px 0 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
}
.messageNotificationsEmpty {
  padding: 40px 0;
}
.unread-badge {
  position: absolute;
  top: 4px;
  right: -2px;
  background: #ff4d4f;
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  padding: 0 5px;
  border-radius: 8px;
  transform: scale(0.85);
}
</style>
